// AI 分析画面（哲学者プロファイル選択 + 分析 + 深掘り対話）
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast, renderLongText, renderHistorySection, bindHistoryHandlers } from '../ui.js';
import { analyzeBook, askFollowup } from '../api.js';
import { getAiCache, setAiCache, ls, saveAiHistory, getAiHistoryByType } from '../cache.js';
import { suggestPhilosophers, allPhilosophers } from '../data/philosophers.js';
import { showQuoteCardScreen } from './quote-card.js';

let _state = null;

export async function showAnalyzeScreen(book) {
  _state = {
    book,
    philosopher: null,
    analysis: null,
    chatHistory: [],
  };
  await renderPicker();
}

async function renderPicker() {
  const { book } = _state;
  const allHist = await getAiHistoryByType('analyze');
  // この書籍に対する過去の分析だけ表示
  const histItems = allHist.filter((h) => h.context?.book?.id === book.id);
  const suggested = suggestPhilosophers(book.genre || [], 4);
  const others = allPhilosophers().filter(
    (p) => !suggested.some((s) => s.name === p.name)
  );

  const lastUsed = ls.getPhiloSelection()[book.id];

  const html = `
    ${screenHead('哲学者の視座を選ぶ')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      「${escapeHtml(book.name)}」のメモを、選んだ思想家の思考様式で分析します。
    </p>
    ${renderHistorySection(histItems)}
    <p class="section-label" style="margin-top:32px;">recommended for this book</p>
    <div class="philo-list" id="philo-recommended">
      ${suggested.map((p) => renderPhiloCard(p, p.name === lastUsed)).join('')}
    </div>
    <details style="margin-top:24px;">
      <summary style="font-family:var(--font-mono);font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:var(--mute);cursor:pointer;padding:12px 0;">
        all philosophers (${others.length})
      </summary>
      <div class="philo-list" id="philo-others">
        ${others.map((p) => renderPhiloCard(p, false)).join('')}
      </div>
    </details>
  `;
  const screen = showScreen('analyze', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelectorAll('[data-philo-name]').forEach((card) => {
    card.addEventListener('click', () => {
      const name = card.dataset.philoName;
      const philo = allPhilosophers().find((p) => p.name === name);
      if (philo) startAnalysis(philo);
    });
  });
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => {
      _state.philosopher = entry.context?.philosopher || { name: '' };
      _state.analysis = entry.payload?.analysis || '';
      renderResult();
    },
    () => renderPicker()
  );
}

function renderPhiloCard(p, isLast) {
  return `
    <div class="philo-card${isLast ? ' philo-card--selected' : ''}" data-philo-name="${escapeHtml(p.name)}">
      <div class="philo-card__body">
        <p class="philo-card__name">${escapeHtml(p.name)}${isLast ? ' （前回使用）' : ''}</p>
        <p class="philo-card__meta">${escapeHtml(p.era)} · ${escapeHtml(p.school)}</p>
        <p class="philo-card__summary">${escapeHtml(p.summary)}</p>
      </div>
      <svg class="icon" style="color:var(--mute)"><use href="#i-arrow-right"/></svg>
    </div>
  `;
}

async function startAnalysis(philosopher) {
  _state.philosopher = philosopher;
  const sel = ls.getPhiloSelection();
  sel[_state.book.id] = philosopher.name;
  ls.setPhiloSelection(sel);

  const cacheKey = `analyze:${_state.book.id}:${philosopher.name}`;
  let analysis = await getAiCache(cacheKey);
  const fromCache = !!analysis;

  if (!analysis) {
    showLoading(`${philosopher.name} の視座で分析中…`);
    try {
      const result = await analyzeBook(_state.book, philosopher);
      analysis = result.analysis;
      await setAiCache(cacheKey, analysis);
    } catch (e) {
      hideLoading();
      console.error(e);
      toast('分析に失敗しました');
      return;
    } finally {
      hideLoading();
    }
  }
  if (!fromCache) {
    await saveAiHistory({
      type: 'analyze',
      title: `${philosopher.name} × ${_state.book.name}`,
      subtitle: philosopher.school || philosopher.era,
      context: { book: _state.book, philosopher },
      payload: { analysis },
    });
  }
  _state.analysis = analysis;
  renderResult();
}

export function renderFromHistory(entry) {
  _state = {
    book: entry.context?.book || {},
    philosopher: entry.context?.philosopher || { name: '' },
    analysis: entry.payload?.analysis || '',
    chatHistory: [],
  };
  // renderResult は private なのでここで再実装は避け、簡易表示用にスクリーンを直接描画
  import('../ui.js').then(({ escapeHtml, screenHead, showScreen, renderLongText }) => {
    const html = `
      ${screenHead('分析結果')}
      <p class="section-label">${escapeHtml(_state.philosopher.name)} の視座から</p>
      <div style="margin-bottom:16px;padding:12px 16px;border:1px solid var(--line);">
        <p style="margin:0 0 4px;font-family:var(--font-serif);font-size:15px;font-weight:500;">${escapeHtml(_state.book.name || '')}</p>
        <p style="margin:0;font-size:12px;color:var(--mute);">${escapeHtml(_state.book.author || '')}</p>
      </div>
      <div class="long-text">${renderLongText(_state.analysis)}</div>
    `;
    const screen = showScreen('analyze-result', html);
    screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  });
}

function renderResult() {
  const { book, philosopher, analysis, chatHistory } = _state;
  const html = `
    ${screenHead('分析結果')}
    <p class="section-label">${escapeHtml(philosopher.name)} の視座から</p>
    <div style="margin-bottom:16px;padding:12px 16px;border:1px solid var(--line);">
      <p style="margin:0 0 4px;font-family:var(--font-serif);font-size:15px;font-weight:500;">${escapeHtml(book.name)}</p>
      <p style="margin:0;font-size:12px;color:var(--mute);">${escapeHtml(book.author || '')}</p>
    </div>
    <div class="long-text">${renderLongText(analysis)}</div>

    <p class="section-label">deepen the dialogue</p>
    <div id="chat-list" class="chat-list">
      ${chatHistory
        .map(
          (m) => `
        <div class="chat-item chat-item--${m.role}">
          <p class="chat-item__role">${m.role === 'user' ? 'you' : escapeHtml(philosopher.name)}</p>
          ${m.role === 'user' ? `<p style="margin:0;">${escapeHtml(m.content)}</p>` : `<div style="white-space:pre-wrap;font-family:var(--font-serif);line-height:1.85;">${escapeHtml(m.content)}</div>`}
        </div>
      `
        )
        .join('')}
    </div>
    <div class="chat-input">
      <textarea id="chat-textarea" placeholder="質問・疑問・別角度の指摘など" rows="2"></textarea>
      <button id="chat-send" class="btn btn--ink" type="button" aria-label="送信">
        <svg class="icon"><use href="#i-arrow-right"/></svg>
      </button>
    </div>

    <div style="margin-top:32px;display:flex;flex-direction:column;gap:8px;">
      <button class="btn btn--block" data-action="quote" type="button">
        <svg class="icon"><use href="#i-quote"/></svg>
        <span>引用カードに書き出す</span>
      </button>
      <button class="btn btn--block btn--ghost" data-action="reselect" type="button">
        他の思想家でも分析する
      </button>
    </div>
  `;
  const screen = showScreen('analyze-result', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('[data-action="reselect"]').addEventListener('click', () => renderPicker());
  screen.querySelector('[data-action="quote"]').addEventListener('click', () =>
    showQuoteCardScreen(book, analysis)
  );
  screen.querySelector('#chat-send').addEventListener('click', sendChat);
  screen.querySelector('#chat-textarea').addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.metaKey) sendChat();
  });
}

async function sendChat() {
  const ta = document.getElementById('chat-textarea');
  const question = (ta.value || '').trim();
  if (!question) return;
  ta.value = '';
  _state.chatHistory.push({ role: 'user', content: question });
  renderResult();
  showLoading('応答を生成中…');
  try {
    const { answer } = await askFollowup(
      _state.book,
      _state.philosopher,
      _state.chatHistory.slice(0, -1),
      question
    );
    _state.chatHistory.push({ role: 'assistant', content: answer });
  } catch (e) {
    toast('応答に失敗しました');
  } finally {
    hideLoading();
    renderResult();
  }
}
